import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

import api from '../../services/api';
import { useWorkspace } from '../../utils/WorkspaceContext';
import { useView } from '../../utils/PersonalViewProvider';
import { rangeForMode } from './calendarUtils';
import MeetingsPanel from './MeetingsPanel';
import DoTodayPanel from './DoTodayPanel';
import TimeLogsPanel from './TimeLogsPanel';
import WeekGrid from './WeekGrid';
import MonthGrid from './MonthGrid';

const MODE_KEY = 'calendar-mode';
const TOGGLES_KEY = 'calendar-toggles';
const DEFAULT_TOGGLES = { meetings: true, do_today: false, time_logs: false };

const readJSON = (key, fallback) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Calendar View page. Answers "what does my day look like" by combining
 * meetings, do-today tasks and time logs over Day/Week/Month layouts, filtered
 * to the selected assignee.
 */
const CalendarPage = () => {
  const { currentWorkspace } = useWorkspace();
  const { selectedAssignee } = useView();

  const [currentDate, setCurrentDate] = useState(dayjs());
  const [mode, setMode] = useState(() => readJSON(MODE_KEY, 'day'));
  const [toggles, setToggles] = useState(() => readJSON(TOGGLES_KEY, DEFAULT_TOGGLES));
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    localStorage.setItem(MODE_KEY, JSON.stringify(mode));
  }, [mode]);
  useEffect(() => {
    localStorage.setItem(TOGGLES_KEY, JSON.stringify(toggles));
  }, [toggles]);

  const activePanels = Object.entries(toggles)
    .filter(([, on]) => on)
    .map(([key]) => key);
  const anyToggleOn = activePanels.length > 0;

  const fetchData = useCallback(async () => {
    if (!currentWorkspace || !anyToggleOn) {
      setData({});
      return;
    }
    setLoading(true);
    try {
      const { date_from, date_to } = rangeForMode(mode, currentDate);
      const params = {
        workspace_id: currentWorkspace.id,
        date_from,
        date_to,
        panels: activePanels.join(','),
      };
      if (selectedAssignee) params.assignee = selectedAssignee;

      const res = await api.get('calendar/', { params });
      setData(res.data?.payload ?? {});
    } catch (error) {
      console.error('Error loading calendar:', error);
      setData({});
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace, mode, currentDate, JSON.stringify(toggles), selectedAssignee]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrev = () => setCurrentDate((d) => d.subtract(1, mode));
  const handleNext = () => setCurrentDate((d) => d.add(1, mode));
  const handleToday = () => setCurrentDate(dayjs());

  const handleToggle = (key) =>
    setToggles((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSelectDay = (day) => {
    setCurrentDate(day);
    setMode('day');
  };

  const dateLabel =
    mode === 'month'
      ? currentDate.format('MMMM YYYY')
      : mode === 'week'
        ? `${currentDate.startOf('week').format('MMM D')} – ${currentDate.endOf('week').format('MMM D, YYYY')}`
        : currentDate.format('dddd, MMM D, YYYY');

  const renderDay = () => {
    if (!anyToggleOn) {
      return (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Select at least one panel (Meetings, Do today, or Time logs).
          </Typography>
        </Paper>
      );
    }
    const cols = 12 / activePanels.length;
    return (
      <Grid container spacing={2} sx={{ height: '100%' }}>
        {toggles.meetings && (
          <Grid size={{ xs: 12, md: cols }}>
            <MeetingsPanel
              currentDate={currentDate}
              events={data?.meetings?.events || []}
              tasks={data?.meetings?.tasks || []}
            />
          </Grid>
        )}
        {toggles.do_today && (
          <Grid size={{ xs: 12, md: cols }}>
            <DoTodayPanel tasks={data?.do_today || []} />
          </Grid>
        )}
        {toggles.time_logs && (
          <Grid size={{ xs: 12, md: cols }}>
            <TimeLogsPanel
              groups={data?.time_logs || []}
              currentDate={currentDate}
              onLogged={fetchData}
            />
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Toolbar */}
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {/* Left: date navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrev} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={handleNext} size="small">
            <ChevronRightIcon />
          </IconButton>
          <Button size="small" onClick={handleToday}>
            Today
          </Button>
          <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 500 }}>
            {dateLabel}
          </Typography>
        </Box>

        {/* Centre: independent toggles */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup size="small">
            <ToggleButton
              value="meetings"
              selected={toggles.meetings}
              onClick={() => handleToggle('meetings')}
            >
              Meetings
            </ToggleButton>
            <ToggleButton
              value="do_today"
              selected={toggles.do_today}
              onClick={() => handleToggle('do_today')}
            >
              Do today
            </ToggleButton>
            <ToggleButton
              value="time_logs"
              selected={toggles.time_logs}
              onClick={() => handleToggle('time_logs')}
            >
              Time logs
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Right: Day / Week / Month */}
        <Select
          size="small"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          <MenuItem value="day">Day</MenuItem>
          <MenuItem value="week">Week</MenuItem>
          <MenuItem value="month">Month</MenuItem>
        </Select>
      </Paper>

      {loading && (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      {mode === 'day' && renderDay()}
      {mode === 'week' && (
        <WeekGrid
          currentDate={currentDate}
          data={data}
          toggles={toggles}
          onSelectDay={handleSelectDay}
        />
      )}
      {mode === 'month' && (
        <MonthGrid
          currentDate={currentDate}
          data={data}
          toggles={toggles}
          onSelectDay={handleSelectDay}
        />
      )}
    </Box>
  );
};

export default CalendarPage;
